import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FaPlus, FaSearch, FaTh, FaList } from 'react-icons/fa'
import { Banner, Button, Card, Checkbox, CollapsibleSection, Column, DataTable, EmptyState, Field, FieldGrid, Input, NativeSelect, ProgressBar, SegmentedControl, Spinner, StatCard, StatGrid, StatusPill, Textarea } from '../../../ui'
import { CategoryMeta } from '../commsInboxView/ReplyDetailPane'
import { formatDateTime } from '../commsInboxView/shared'
import { MailboxRef } from '../commsShared/mailboxes'
import { ExportGroupBy, downloadCommsExport } from '../commsShared/exportDownload'
import { DeepDiveScope, DeepDiveStatus, EvidenceState, MergeSuggestion, Subcategory, SuggestedAction, deepDiveApi, messageOf, minutesText, pounds, suggestMerges } from '../commsShared/deepDive'
import { ReportMenu } from '../commsMailboxWorkspaceView/CategoryReport'
import { DeepDiveConfirm } from './DeepDiveConfirm'
import { ACTION_TYPES, FeaturePill, SubcategoryDetail, TIMING_OPTIONS, requirementLine } from './SubcategoryDetail'
import { SubcategoryThreads } from './SubcategoryThreads'
import styles from './styles.module.scss'

interface Props {
    scope: DeepDiveScope
    /** For the modal header and export filters. */
    scopeLabel: string
    letterKind: string | null
    bucket: string
    /** For the thread modal: category labels and mailbox names. */
    categoryMeta?: Map<string, CategoryMeta>
    mailboxes?: MailboxRef[]
}

const fmt = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-GB'))
const POLL_MS = 4000

type SubView = 'cards' | 'table'
const SUB_VIEW_KEY = 'comms.deepDive.subView'
const readSubView = (): SubView => (typeof window !== 'undefined' && window.localStorage.getItem(SUB_VIEW_KEY) === 'table' ? 'table' : 'cards')
const DISMISSED_KEY = 'comms.deepDive.dismissedMerges'
const pairKey = (a: string, b: string) => [a, b].sort().join('|')
const readDismissed = (): Set<string> => {
    try {
        return new Set(JSON.parse(window.localStorage.getItem(DISMISSED_KEY) || '[]'))
    } catch {
        return new Set()
    }
}

interface SubcategoryDraft {
    label: string
    description: string
    actionType: string
    actionLabel: string
    actionDescription: string
    timing: string
}

const draftOf = (s?: Subcategory | null): SubcategoryDraft => ({
    label: s?.label ?? '',
    description: s?.description ?? '',
    actionType: s?.suggestedAction?.type ?? '',
    actionLabel: s?.suggestedAction?.label ?? '',
    actionDescription: s?.suggestedAction?.description ?? '',
    timing: s?.suggestedAction?.timing ?? '',
})

const actionOf = (d: SubcategoryDraft): Partial<SuggestedAction> | null => (d.actionLabel.trim() ? { type: d.actionType || null, label: d.actionLabel.trim(), description: d.actionDescription.trim() || null, timing: d.timing || null } : null)

/** Add or edit one sub-category: its name, meaning and the requirement for the feature that will handle it. */
const SubcategoryForm = ({ initial, saving, onSave, onCancel, submitLabel }: { initial: SubcategoryDraft; saving: boolean; onSave: (d: SubcategoryDraft) => void; onCancel: () => void; submitLabel: string }) => {
    const [d, setD] = useState(initial)
    const set = (k: keyof SubcategoryDraft) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setD((p) => ({ ...p, [k]: e.target.value }))
    return (
        <form
            className={styles.subForm}
            onSubmit={(e) => {
                e.preventDefault()
                if (d.label.trim()) onSave(d)
            }}
        >
            <FieldGrid minColumn={220}>
                <Field label="Sub-category" required>
                    <Input value={d.label} onChange={set('label')} maxLength={120} placeholder="e.g. Photo ID" autoFocus />
                </Field>
                <Field label="What it means" span={2}>
                    <Input value={d.description} onChange={set('description')} maxLength={400} placeholder="One line a colleague would recognise" />
                </Field>
            </FieldGrid>
            <FieldGrid minColumn={180}>
                <Field label="Requirement" hint="The outcome a feature for this sub-category must deliver">
                    <Input value={d.actionLabel} onChange={set('actionLabel')} maxLength={120} placeholder="e.g. Collect photo ID from the client and send it to the lender" />
                </Field>
                <Field label="Kind">
                    <NativeSelect value={d.actionType} onChange={set('actionType')} options={ACTION_TYPES} />
                </Field>
                <Field label="When">
                    <NativeSelect value={d.timing} onChange={set('timing')} options={TIMING_OPTIONS} />
                </Field>
            </FieldGrid>
            <Field label="How it should work" optional>
                <Textarea value={d.actionDescription} onChange={set('actionDescription')} rows={2} maxLength={400} placeholder="Steps, data needed, who it goes to - anything the developer needs" />
            </Field>
            <div className={styles.formActions}>
                <Button type="submit" size="sm" loading={saving} disabled={!d.label.trim()}>
                    {submitLabel}
                </Button>
                <Button type="button" size="sm" variant="neutral" onClick={onCancel} disabled={saving}>
                    Cancel
                </Button>
            </div>
        </form>
    )
}

/**
 * Deep dive on one thread outcome: run it (with a preview and a spend cap),
 * watch it, then work with what it found — the sub-categories under this
 * outcome, each with its suggested action and its threads, every thread
 * carrying the quoted wording from the email that put it there.
 */
export const DeepDiveCard = ({ scope, scopeLabel, letterKind, bucket, categoryMeta, mailboxes }: Props) => {
    const [status, setStatus] = useState<DeepDiveStatus | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [stopping, setStopping] = useState(false)
    const [selected, setSelected] = useState<string | null>(null)
    const [evidenceFilter, setEvidenceFilter] = useState<'all' | EvidenceState>('all')
    const [flaggedOnly, setFlaggedOnly] = useState(false)
    const [showThreads, setShowThreads] = useState(false)
    const [subsOpen, setSubsOpen] = useState(true)
    const [adding, setAdding] = useState(false)
    const [editing, setEditing] = useState<string | null>(null)
    const threadsRef = useRef<HTMLDivElement>(null)
    const [saving, setSaving] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [version, setVersion] = useState(0)
    const [actionError, setActionError] = useState<string | null>(null)
    const [subView, setSubViewState] = useState<SubView>(readSubView)
    const [dismissed, setDismissed] = useState<Set<string>>(readDismissed)
    const [showAllSuggestions, setShowAllSuggestions] = useState(false)
    const setSubView = (v: SubView) => {
        setSubViewState(v)
        window.localStorage.setItem(SUB_VIEW_KEY, v)
    }
    const dismissSuggestion = (m: MergeSuggestion) => {
        const next = new Set(dismissed)
        next.add(pairKey(m.from.id, m.into.id))
        setDismissed(next)
        window.localStorage.setItem(DISMISSED_KEY, JSON.stringify(Array.from(next)))
    }

    const load = useCallback(async () => {
        try {
            const s = await deepDiveApi.status(scope)
            setStatus(s)
            setError(null)
        } catch (e) {
            setError(messageOf(e, 'Could not load the deep dive'))
        }
    }, [scope])

    useEffect(() => {
        setStatus(null)
        setSelected(null)
        setShowThreads(false)
        load()
    }, [load])

    const running = Boolean(status?.job?.running && status.job.thisParent)
    const runningElsewhere = Boolean(status?.job?.running && !status.job.thisParent)

    useEffect(() => {
        if (!running) return
        const t = setInterval(() => {
            load()
            setVersion((v) => v + 1)
        }, POLL_MS)
        return () => clearInterval(t)
    }, [running, load])

    const refresh = () => {
        load()
        setVersion((v) => v + 1)
    }

    const subs = useMemo(() => (status?.subcategories ?? []).filter((s) => s.active && !s.mergedIntoId).sort((a, b) => (b.threads ?? 0) - (a.threads ?? 0)), [status])
    const selectedSub = selected ? subs.find((s) => s.id === selected) ?? null : null
    const suggestions = useMemo(() => suggestMerges(subs).filter((m) => !dismissed.has(pairKey(m.from.id, m.into.id))), [subs, dismissed])

    const stop = async () => {
        setStopping(true)
        try {
            await deepDiveApi.stop()
            setTimeout(refresh, 800)
        } catch (e) {
            setActionError(messageOf(e, 'Could not stop the deep dive'))
        } finally {
            setStopping(false)
        }
    }

    const wrap = async (fn: () => Promise<unknown>) => {
        setSaving(true)
        setActionError(null)
        try {
            await fn()
            refresh()
            return true
        } catch (e) {
            setActionError(messageOf(e, 'That did not save'))
            return false
        } finally {
            setSaving(false)
        }
    }

    const create = (d: SubcategoryDraft) =>
        wrap(() => deepDiveApi.createSubcategory(scope, { label: d.label.trim(), description: d.description.trim() || null, suggestedAction: actionOf(d) })).then((ok) => ok && setAdding(false))
    const update = (id: string, d: SubcategoryDraft) =>
        wrap(() => deepDiveApi.updateSubcategory(id, { label: d.label.trim(), description: d.description.trim() || null, suggestedAction: actionOf(d) })).then((ok) => ok && setEditing(null))
    const merge = (fromId: string, intoId: string) =>
        wrap(() => deepDiveApi.mergeSubcategory(fromId, intoId)).then((ok) => {
            if (ok && selected === fromId) setSelected(intoId)
        })

    const exportReport = async (groupBy: ExportGroupBy | null) => {
        setExporting(true)
        try {
            await downloadCommsExport(
                {
                    kind: 'thread_subcategory',
                    category: scope.parent,
                    sendKind: letterKind,
                    mailbox: scope.mailbox,
                    includeArchived: bucket === 'all',
                    archivedOnly: bucket === 'archived',
                    includeUnlinked: true,
                    groupBy,
                    subcategoryId: selectedSub?.id ?? null,
                    subcategoryLabel: selectedSub?.label ?? null,
                    evidenceState: evidenceFilter === 'all' ? null : evidenceFilter,
                    flaggedOnly,
                },
                `comms-deep-dive-${scope.parent}${selectedSub ? `-${selectedSub.key}` : ''}${groupBy ? `-by-${groupBy}` : ''}.xlsx`
            )
        } finally {
            setExporting(false)
        }
    }

    /** Open the threads section for one sub-category (or all) and bring it into view. */
    const openThreads = (id: string | null) => {
        setSelected(id)
        setShowThreads(true)
        setTimeout(() => threadsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
    }
    /** Expand the details of one sub-category (click again to close). */
    const pickSub = (id: string) => {
        setEditing(null)
        setSelected((cur) => (cur === id ? null : id))
    }
    const detailFor = (s: Subcategory) =>
        editing === s.id ? (
            <div className={styles.detail} onClick={(e) => e.stopPropagation()}>
                <SubcategoryForm initial={draftOf(s)} saving={saving} onSave={(d) => update(s.id, d)} onCancel={() => setEditing(null)} submitLabel="Save" />
            </div>
        ) : (
            <SubcategoryDetail scope={scope} sub={s} all={subs} saving={saving} exporting={exporting} onEdit={() => setEditing(s.id)} onMerge={(into) => merge(s.id, into)} onExport={exportReport} onViewThreads={() => openThreads(s.id)} onClose={() => setSelected(null)} />
        )

    if (error) return <Card title="Deep dive">{<EmptyState bare tone="error" title="Could not load the deep dive" description={error} action={<Button size="sm" onClick={load}>Try again</Button>} />}</Card>
    if (!status) return <Card title="Deep dive">{<Spinner block text="Loading…" />}</Card>

    if (!status.parent.affectsOutcome) {
        return (
            <Card title="Deep dive" subtitle="Not available for this group">
                <p className={styles.muted}>
                    <strong>{status.parent.label}</strong> never sets where a thread stands (it is automated mail or our own email), so there is no outcome email to read more closely.
                </p>
            </Card>
        )
    }

    const { counts, lastRun, job, runner } = status
    const neverRun = counts.labelled === 0 && !lastRun && !running
    const live = job?.progress?.live ?? null
    const planned = live?.planned ?? (typeof job?.params?.maxItems === 'number' ? (job.params.maxItems as number) : null) ?? (lastRun && !lastRun.finishedAt ? lastRun.threadsPlanned : null) ?? counts.pending + (job?.progress?.processed ?? 0)
    const threadsWord = (n: number) => `thread${n === 1 ? '' : 's'}`

    const runButton = (label: string) => (
        <Button size="sm" icon={<FaSearch aria-hidden />} onClick={() => setConfirmOpen(true)} disabled={!runner.canRun || running || runningElsewhere} title={!runner.canRun ? (runner.reason === 'worker_required' ? 'Set "Who runs the pipeline" to Worker on the Pipeline page' : 'The comms worker cannot be reached') : runningElsewhere ? 'A deep dive is already running on another outcome' : 'Preview what will be sent, then start'}>
            {label}
        </Button>
    )

    return (
        <>
            <Card
                title="Deep dive"
                subtitle={`What exactly the ${threadsWord(2)} at "${status.parent.label}" say, as sub-categories with the email's own wording as evidence.`}
                actions={
                    <div className={styles.cardActions}>
                        {!neverRun && counts.labelled > 0 && <ReportMenu unit="thread" variant="ghost" exporting={exporting} onExport={exportReport} label={selectedSub ? `Report · ${selectedSub.label}` : 'Report'} />}
                        {!neverRun && !running && counts.pending > 0 && runButton(`Deep dive ${fmt(counts.pending)} new…`)}
                        {!neverRun && !running && counts.pending === 0 && runButton('Re-run…')}
                        {running && (
                            <Button size="sm" variant="danger" onClick={stop} loading={stopping}>
                                Stop
                            </Button>
                        )}
                    </div>
                }
            >
                {!runner.canRun && !running && (
                    <Banner tone="warn" title={runner.reason === 'worker_required' ? 'The deep dive runs on the comms worker only' : 'The comms worker cannot be reached'}>
                        {runner.reason === 'worker_required' ? 'On the Pipeline page, under Comms settings, set "Who runs the pipeline" to Worker. Everything already found is still shown below.' : 'The worker did not answer its health check. Runs cannot start until it is back; everything already found is still shown below.'}
                    </Banner>
                )}
                {runningElsewhere && (
                    <Banner tone="info">A deep dive is running on another outcome ({job?.parent?.replace(/_/g, ' ')}). One runs at a time; this one can start when it finishes.</Banner>
                )}
                {actionError && (
                    <Banner tone="bad" actions={<Button size="sm" variant="ghost" onClick={() => setActionError(null)}>Dismiss</Button>}>
                        {actionError}
                    </Banner>
                )}

                {running && job?.progress && (
                    <div className={styles.runBox}>
                        <ProgressBar value={job.progress.processed} max={Math.max(planned || 0, job.progress.processed, 1)} label={`${fmt(job.progress.processed)} of ${fmt(planned)} ${threadsWord(planned || 0)} read · ${pounds(job.progress.costPence)} so far`} tone="accent" />
                        <div className={styles.runLine}>
                            <span>
                                <strong>{fmt(job.progress.ok)}</strong> labelled
                            </span>
                            {job.progress.failed > 0 && (
                                <span className={styles.bad}>
                                    <strong>{fmt(job.progress.failed)}</strong> failed
                                </span>
                            )}
                            <span>
                                <strong>{fmt(live?.newSubcategories ?? 0)}</strong> new sub-categories
                            </span>
                            <span>
                                <strong>{fmt(live?.needsCheck ?? 0)}</strong> need a check
                            </span>
                            {(live?.flagged ?? 0) > 0 && (
                                <span className={styles.warn}>
                                    <strong>{fmt(live?.flagged)}</strong> flagged
                                </span>
                            )}
                            <span className={styles.muted}>Started {formatDateTime(job.startedAt)} · updates every few seconds</span>
                        </div>
                    </div>
                )}

                {neverRun ? (
                    <EmptyState
                        bare
                        icon={<FaSearch />}
                        title={`Not yet run for ${status.parent.label}`}
                        description={
                            <>
                                <strong>{fmt(counts.threads)}</strong> {threadsWord(counts.threads)} stand here. The deep dive reads the email that put each one here and returns what exactly it says, as sub-categories with a suggested next action, and quotes the email's own wording as evidence for every label. You will see how many threads, the estimated cost and time, and set a spend cap before anything runs.
                            </>
                        }
                        action={counts.threads > 0 ? runButton(`Deep dive ${fmt(counts.threads)} ${threadsWord(counts.threads)}…`) : undefined}
                    />
                ) : (
                    <>
                        <StatGrid minColumn={150}>
                            <StatCard compact label="Threads here" value={counts.threads} />
                            <StatCard compact label="Labelled" value={counts.labelled} tone="good" sub={counts.threads ? `${Math.round((counts.labelled / counts.threads) * 100)}% of threads` : undefined} active={selected === null && showThreads && evidenceFilter === 'all' && !flaggedOnly} onClick={() => { setEvidenceFilter('all'); setFlaggedOnly(false); openThreads(null) }} />
                            <StatCard compact label="Still to do" value={counts.pending} tone={counts.pending > 0 ? 'warn' : 'muted'} sub={counts.pending > 0 ? 'new or re-opened threads' : 'all read'} />
                            <StatCard compact label="Need a check" value={counts.needsCheck} tone={counts.needsCheck > 0 ? 'warn' : 'muted'} sub="quote not found as written" active={evidenceFilter === 'needs_check' && showThreads} onClick={() => { setEvidenceFilter('needs_check'); setFlaggedOnly(false); openThreads(null) }} />
                            <StatCard compact label="Flagged" value={counts.flagged} tone={counts.flagged > 0 ? 'bad' : 'muted'} sub="injection or cost outliers" active={flaggedOnly && showThreads} onClick={() => { setFlaggedOnly(true); setEvidenceFilter('all'); openThreads(null) }} />
                        </StatGrid>

                        {lastRun && (
                            <p className={styles.lastRun}>
                                <strong>Last run</strong> {formatDateTime(lastRun.startedAt)}
                                {lastRun.startedBy ? ` by ${lastRun.startedBy}` : ''} · {fmt(lastRun.processed)} {threadsWord(lastRun.processed)}
                                {lastRun.threadsPlanned != null && lastRun.threadsPlanned !== lastRun.processed ? ` of ${fmt(lastRun.threadsPlanned)} planned` : ''} · {pounds(lastRun.costPence)}
                                {lastRun.estCostPence != null ? ` (estimated ${pounds(lastRun.estCostPence, { approx: true })})` : ''} · {lastRun.durationMs != null ? minutesText(Math.round(lastRun.durationMs / 60000)) : 'still running'}
                                {lastRun.estMinutes != null ? ` (estimated ${minutesText(lastRun.estMinutes)})` : ''} · {fmt(lastRun.newSubcategories)} new sub-categories
                                {lastRun.reason && lastRun.reason !== 'complete' && (
                                    <>
                                        {' '}
                                        <StatusPill size="sm" tone={lastRun.reason === 'error' ? 'bad' : 'warn'} title={lastRun.error ?? undefined}>
                                            {lastRun.reason === 'budget' ? 'stopped at the spend cap' : lastRun.reason === 'stopped' ? 'stopped by hand' : 'ended with an error'}
                                        </StatusPill>
                                    </>
                                )}
                            </p>
                        )}

                        <CollapsibleSection
                            variant="flat"
                            level="h3"
                            hideHint
                            title="Sub-categories"
                            meta={`${fmt(subs.length)} · click one for its details and requirements`}
                            open={subsOpen}
                            onToggle={setSubsOpen}
                            actions={
                                <div className={styles.cardActions}>
                                    <SegmentedControl<SubView>
                                        value={subView}
                                        onChange={setSubView}
                                        aria-label="Show sub-categories as"
                                        options={[
                                            { value: 'cards', label: (<><FaTh aria-hidden /> Cards</>) },
                                            { value: 'table', label: (<><FaList aria-hidden /> Table</>) },
                                        ]}
                                    />
                                    <Button size="sm" variant="ghost" icon={<FaPlus aria-hidden />} onClick={() => setAdding((v) => !v)} disabled={saving}>
                                        Add sub-category
                                    </Button>
                                </div>
                            }
                        >
                            {suggestions.length > 0 && (
                                <div className={styles.suggestBox}>
                                    <div className={styles.suggestHead}>
                                        <strong>Possible duplicates</strong>
                                        <span className={styles.muted}>
                                            {' '}
                                            · {fmt(suggestions.length)} pair{suggestions.length === 1 ? '' : 's'} whose names say much the same thing. Merging moves every thread across and retires the smaller one; it can be undone by moving threads back.
                                        </span>
                                    </div>
                                    <ul className={styles.suggestList}>
                                        {(showAllSuggestions ? suggestions : suggestions.slice(0, 3)).map((m) => (
                                            <li key={pairKey(m.from.id, m.into.id)} className={styles.suggestRow}>
                                                <span className={styles.suggestPair}>
                                                    <strong>{m.from.label}</strong> <span className={styles.muted}>({fmt(m.from.threads ?? 0)})</span> <span className={styles.muted}>→</span> <strong>{m.into.label}</strong> <span className={styles.muted}>({fmt(m.into.threads ?? 0)})</span>
                                                    {m.shared.length > 0 && <span className={styles.suggestWhy}>share: {m.shared.join(', ')}</span>}
                                                </span>
                                                <span className={styles.suggestActions}>
                                                    <Button size="sm" onClick={() => window.confirm(`Move every thread from "${m.from.label}" into "${m.into.label}" and retire "${m.from.label}"?`) && merge(m.from.id, m.into.id)} disabled={saving} title={`Keep "${m.into.label}"`}>
                                                        Merge
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={() => window.confirm(`Move every thread from "${m.into.label}" into "${m.from.label}" and retire "${m.into.label}"?`) && merge(m.into.id, m.from.id)} disabled={saving} title={`Keep "${m.from.label}" instead`}>
                                                        Merge the other way
                                                    </Button>
                                                    <Button size="sm" variant="link" onClick={() => dismissSuggestion(m)} title="Not the same thing; do not suggest this pair again on this browser">
                                                        Not a duplicate
                                                    </Button>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                    {suggestions.length > 3 && (
                                        <Button size="sm" variant="link" onClick={() => setShowAllSuggestions((v) => !v)}>
                                            {showAllSuggestions ? 'Show fewer' : `Show ${fmt(suggestions.length - 3)} more`}
                                        </Button>
                                    )}
                                </div>
                            )}
                            {adding && (
                                <Card padding="sm" className={styles.subFormCard}>
                                    <SubcategoryForm initial={draftOf(null)} saving={saving} onSave={create} onCancel={() => setAdding(false)} submitLabel="Add" />
                                </Card>
                            )}

                            {subs.length === 0 ? (
                                <p className={styles.muted}>No sub-categories yet.</p>
                            ) : subView === 'table' ? (
                                <DataTable<Subcategory>
                                    dense
                                    rows={subs}
                                    rowKey={(s) => s.id}
                                    selectedKey={selected}
                                    onRowClick={(s) => pickSub(s.id)}
                                    expandedKeys={selected ? [selected] : []}
                                    renderExpanded={detailFor}
                                    columns={
                                        [
                                            { key: 'label', header: 'Sub-category', truncate: true, sortable: true, sortValue: (s) => s.label, render: (s) => (
                                                <span className={styles.tableLabel} title={s.description ?? undefined}>
                                                    <strong>{s.label}</strong>
                                                    {s.description && <span className={styles.muted}>{s.description}</span>}
                                                </span>
                                            ) },
                                            { key: 'threads', header: 'Threads', align: 'right', nowrap: true, width: 190, sortable: true, sortValue: (s) => s.threads ?? 0, render: (s) => (
                                                <span className={styles.tableStack}>
                                                    <strong>{fmt(s.threads ?? 0)}</strong>
                                                    <span className={styles.muted}>{fmt(s.linked ?? 0)} with a client or claim</span>
                                                </span>
                                            ) },
                                            { key: 'checks', header: 'Checks', align: 'right', nowrap: true, width: 130, sortable: true, sortValue: (s) => (s.needsCheck ?? 0) * 1000 + (s.flagged ?? 0), render: (s) => (
                                                <span className={styles.tableStack}>
                                                    <span className={(s.needsCheck ?? 0) > 0 ? styles.warn : styles.muted}>{fmt(s.needsCheck ?? 0)} need a check</span>
                                                    <span className={(s.flagged ?? 0) > 0 ? styles.bad : styles.muted}>{fmt(s.flagged ?? 0)} flagged</span>
                                                </span>
                                            ) },
                                            { key: 'feature', header: 'Feature', truncate: true, width: '28%', sortable: true, sortValue: (s) => requirementLine(s.suggestedAction) ?? '', render: (s) => (
                                                <span className={`${styles.tableStack} ${styles.tableFeature}`} title={requirementLine(s.suggestedAction) ?? undefined}>
                                                    <FeaturePill />
                                                    <span className={styles.muted}>{requirementLine(s.suggestedAction) ?? 'requirement not stated'}</span>
                                                </span>
                                            ) },
                                        ] as Column<Subcategory>[]
                                    }
                                />
                            ) : (
                                <>
                                    <div className={styles.subGrid}>
                                        {subs.map((s) => {
                                            const isSel = selected === s.id
                                            const isUnsorted = s.key === 'unsorted'
                                            return (
                                                <button key={s.id} type="button" className={`${styles.subCard} ${isSel ? styles.subCardActive : ''} ${isUnsorted ? styles.subCardUnsorted : ''}`} onClick={() => pickSub(s.id)} aria-expanded={isSel} title={isSel ? 'Close the details' : 'Show details, requirements and actions'}>
                                                    <div className={styles.subTop}>
                                                        <span className={styles.subLabel}>{s.label}</span>
                                                        <span className={styles.subCount}>{fmt(s.threads ?? 0)}</span>
                                                    </div>
                                                    {s.description && <p className={styles.subDesc}>{s.description}</p>}
                                                    <ul className={styles.subStats}>
                                                        <li>
                                                            <strong>{fmt(s.linked ?? 0)}</strong> with a client or claim
                                                        </li>
                                                        {(s.needsCheck ?? 0) > 0 && (
                                                            <li className={styles.warn}>
                                                                <strong>{fmt(s.needsCheck)}</strong> need a check
                                                            </li>
                                                        )}
                                                        {(s.flagged ?? 0) > 0 && (
                                                            <li className={styles.bad}>
                                                                <strong>{fmt(s.flagged)}</strong> flagged
                                                            </li>
                                                        )}
                                                    </ul>
                                                    <div className={styles.subFoot}>
                                                        <StatusPill size="sm" tone={s.source === 'human' ? 'accent' : 'neutral'} title={s.source === 'human' ? `Added by ${s.createdBy ?? 'a person'}` : 'Proposed by the model'}>
                                                            {s.source === 'human' ? 'ours' : 'model'}
                                                        </StatusPill>
                                                        <FeaturePill />
                                                    </div>
                                                </button>
                                            )
                                        })}
                                    </div>
                                    {selectedSub && detailFor(selectedSub)}
                                </>
                            )}
                        </CollapsibleSection>

                        {counts.labelled > 0 && (
                            <div ref={threadsRef} className={styles.threadsAnchor}>
                                <CollapsibleSection
                                    variant="flat"
                                    level="h3"
                                    hideHint
                                    title={selectedSub ? `Items · ${selectedSub.label}` : 'Items · every labelled thread'}
                                    meta={showThreads ? 'the wording from each email; click a row to open it' : `${fmt(selectedSub?.threads ?? counts.labelled)} threads`}
                                    open={showThreads}
                                    onToggle={setShowThreads}
                                    actions={
                                        showThreads ? (
                                            <div className={styles.threadFilters}>
                                                {selectedSub && (
                                                    <Button size="sm" variant="link" onClick={() => setSelected(null)}>
                                                        Show every sub-category
                                                    </Button>
                                                )}
                                                <SegmentedControl<'all' | EvidenceState>
                                                    value={evidenceFilter}
                                                    onChange={setEvidenceFilter}
                                                    options={[
                                                        { value: 'all', label: 'All' },
                                                        { value: 'verified', label: 'Verified' },
                                                        { value: 'needs_check', label: 'Needs a check' },
                                                        { value: 'human', label: 'Set by a person' },
                                                    ]}
                                                    aria-label="Evidence state"
                                                />
                                                <Checkbox label="Flagged only" checked={flaggedOnly} onChange={(e) => setFlaggedOnly(e.target.checked)} />
                                            </div>
                                        ) : undefined
                                    }
                                >
                                    <SubcategoryThreads scope={scope} subcategories={subs} subcategoryId={selectedSub?.id ?? null} evidenceState={evidenceFilter === 'all' ? null : evidenceFilter} flaggedOnly={flaggedOnly} version={version} onChanged={refresh} categoryMeta={categoryMeta} mailboxes={mailboxes} />
                                </CollapsibleSection>
                            </div>
                        )}
                    </>
                )}
            </Card>

            <DeepDiveConfirm open={confirmOpen} onClose={() => setConfirmOpen(false)} scope={scope} parentLabel={status.parent.label} scopeLabel={scopeLabel} runner={runner} pending={counts.pending} labelled={counts.labelled} onStarted={() => setTimeout(refresh, 600)} />
        </>
    )
}
